'use client'

import { Loader } from '@mantine/core'
import { RebrandSearchIcon } from '@/components/Icons/Rebrand/SearchIcon'
import classes from './UniversalSearch.module.css'

/**
 * Visual style for each search surface:
 * - inlineClosed — search bar in the page before search opens (desktop header or mobile inline)
 * - inlineOpen — input inside the open desktop dropdown (borderless; the panel has the chrome)
 * - dock — mobile bar pinned to the bottom of the screen
 */
export type SearchInputVariant = 'inlineClosed' | 'inlineOpen' | 'dock'

interface SearchInputFieldProps {
    /** Current search text. */
    value: string
    /** Hint text shown when the field is empty. */
    placeholder: string
    /** Called as the user types. */
    onChange: (value: string) => void
    /** Called when the field receives focus (for example, to open the mobile panel). */
    onFocus?: () => void
    /** Keyboard handler for Enter submit and Escape close. */
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    /** Ref for the underlying input, used to focus the active search input and prevent automatic page scrolling. */
    inputRef?: React.Ref<HTMLInputElement | null>
    /** Focus the input on mount (used when the desktop dropdown opens). */
    autoFocus?: boolean
    /** Accessible name for the input. Defaults to "Search". */
    ariaLabel?: string
    /** Controls borders, padding, and whether the search icon is highlighted. */
    variant?: SearchInputVariant
    /** Render a non-interactive field for server-rendered placeholders. */
    presentationOnly?: boolean
    /** Show a loading indicator after the text while a submitted search loads. */
    isLoading?: boolean
    tabIndex?: number
}

/** Search text field with a leading search icon. */
export function SearchInputField({
    value,
    placeholder,
    onChange,
    onFocus,
    onKeyDown,
    inputRef,
    autoFocus = false,
    ariaLabel = 'Search',
    variant = 'inlineClosed',
    presentationOnly = false,
    isLoading = false,
    tabIndex,
}: SearchInputFieldProps) {
    const isInlineOpen = variant === 'inlineOpen'
    const isDock = variant === 'dock'
    const iconSize = 16
    const isIconHighlighted = isInlineOpen || (isDock && value.length > 0)

    const inputFieldClassName = isInlineOpen
        ? classes.inputFieldEmbedded
        : isDock
          ? classes.inputFieldDock
          : classes.inputFieldCollapsed

    return (
        <div
            className={[classes.inputField, inputFieldClassName].filter(Boolean).join(' ')}
            data-search-collapsed-field={
                variant === 'inlineClosed' && !presentationOnly ? '' : undefined
            }
            aria-hidden={presentationOnly || undefined}
            style={presentationOnly ? { pointerEvents: 'none' } : undefined}
        >
            <RebrandSearchIcon
                width={iconSize}
                height={iconSize}
                className={`${classes.inputIcon} ${isIconHighlighted ? classes.inputIconFocused : ''}`}
                aria-hidden
            />
            <div className={classes.inputWrapper}>
                <input
                    ref={inputRef as React.LegacyRef<HTMLInputElement>}
                    type="text"
                    className={[classes.input, isDock ? classes.inputDock : '']
                        .filter(Boolean)
                        .join(' ')}
                    value={value}
                    placeholder={placeholder}
                    onChange={(event) => onChange(event.target.value)}
                    onFocus={presentationOnly ? undefined : onFocus}
                    onKeyDown={presentationOnly ? undefined : onKeyDown}
                    autoFocus={!presentationOnly && autoFocus}
                    readOnly={presentationOnly}
                    tabIndex={presentationOnly ? -1 : tabIndex}
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    aria-label={ariaLabel}
                />
            </div>
            {isLoading && (
                <span role="status" aria-label="Loading search results" className={classes.inputLoader}>
                    <Loader size={16} color="currentColor" />
                </span>
            )}
        </div>
    )
}
